import React, { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { makeApiRequest, apiRoutes } from "constants/apiRoutes";
import { useEffectiveCompanyId } from "hooks/useEffectiveCompanyId";
import { useAuth } from "utilities/auth";
import { isInternal } from "utilities/userType";
import { useToast } from "components/Toast";
import Skeleton from "components/Skeleton";
import { StatusBadge } from "components/Account/StatusBadge";
import EntityTicketsSection from "components/Tickets/EntityTicketsSection";
import {
  Shield,
  Plus,
  Save,
  X,
  CheckCircle,
  AlertCircle,
  XCircle,
  CreditCard,
  Info,
} from "lucide-react";

const PROGRAMS = ["rdr", "cdrn", "ethoca", "tc40"];

function ProgramBadge({ status, program, enrolled, midStatus }) {
  // TC40 uses binary Activated/Inactive — Disputifier doesn't track its status
  if (program === "tc40") {
    const isActivated = enrolled && midStatus !== "closed";
    return (
      <span className={`inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-xs font-medium ${
        isActivated ? "bg-success/20 text-success" : "bg-muted text-muted-foreground"
      }`}>
        {isActivated ? <><CheckCircle className="h-3 w-3" /> Activated</> : <><XCircle className="h-3 w-3" /> Inactive</>}
      </span>
    );
  }

  const s = (status || "").toLowerCase();
  if (s === "live") {
    return (
      <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-xs font-medium bg-success/20 text-success">
        <CheckCircle className="h-3 w-3" /> Live
      </span>
    );
  }
  if (s === "in progress" || s === "submitted") {
    return (
      <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-xs font-medium bg-warning/20 text-warning">
        <AlertCircle className="h-3 w-3" /> In Progress
      </span>
    );
  }
  return (
    <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-xs font-medium bg-muted text-muted-foreground">
      <XCircle className="h-3 w-3" /> —
    </span>
  );
}

export default function DisputifierTab({ companyId }) {
  const { user } = useAuth();
  const effectiveCompanyId = useEffectiveCompanyId();
  const isAdmin = isInternal(user);
  const navigate = useNavigate();
  const { toast } = useToast();

  const [merchants, setMerchants] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showEnrollForm, setShowEnrollForm] = useState(false);
  const [dbas, setDbas] = useState([]);
  const [mids, setMids] = useState([]);
  const [submitting, setSubmitting] = useState(false);
  const [enrollForm, setEnrollForm] = useState({
    dbaId: "",
    midId: "",
    email: "",
    thresholdAmount: "",
    programs: { rdr: true, cdrn: true, ethoca: true, tc40: true },
  });

  const authObj = { id: user.id, accessToken: user.accessToken };

  const fetchMerchants = async () => {
    setLoading(true);
    try {
      const res = await makeApiRequest(
        "get",
        apiRoutes.disputifierListMerchants,
        authObj,
        effectiveCompanyId
      );
      const all = res.data?.data || [];
      setMerchants(all.filter((m) => m.companyId === companyId));
    } catch (err) {
      console.error("Failed to load Disputifier merchants", err);
    } finally {
      setLoading(false);
    }
  };

  const fetchDbas = async () => {
    try {
      const res = await makeApiRequest(
        "get",
        apiRoutes.getApplication(companyId),
        authObj,
        effectiveCompanyId
      );
      setDbas(res.data?.data?.dbas || res.data?.dbas || []);
    } catch {
      setDbas([]);
    }
  };

  const fetchMids = async () => {
    try {
      const res = await makeApiRequest(
        "get",
        `${apiRoutes.listMids}?companyId=${companyId}`,
        authObj,
        effectiveCompanyId
      );
      setMids(res.data?.data || []);
    } catch {
      setMids([]);
    }
  };

  useEffect(() => {
    if (companyId) fetchMerchants();
  }, [companyId]);

  const handleEnroll = async () => {
    if (!enrollForm.dbaId || !enrollForm.midId) return;
    setSubmitting(true);
    try {
      await makeApiRequest("post", apiRoutes.disputifierEnrollMerchant, authObj, effectiveCompanyId, {
        companyId,
        dbaId: enrollForm.dbaId,
        midId: enrollForm.midId,
        email: enrollForm.email || undefined,
        thresholdAmount: enrollForm.thresholdAmount ? Number(enrollForm.thresholdAmount) : undefined,
        programs: enrollForm.programs,
      });
      toast({ status: "success", title: "DBA enrolled in Disputifier" });
      setShowEnrollForm(false);
      setEnrollForm({ dbaId: "", midId: "", email: "", thresholdAmount: "", programs: { rdr: true, cdrn: true, ethoca: true, tc40: true } });
      await fetchMerchants();
    } catch (err) {
      toast({
        status: "destructive",
        title: "Enrollment failed",
        description: err.response?.data?.error || "Unknown error",
      });
    } finally {
      setSubmitting(false);
    }
  };

  if (loading) {
    return (
      <div className="space-y-4">
        <Skeleton.Rect className="h-32 w-full" />
        <Skeleton.Rect className="h-32 w-full" />
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* Header + enroll button */}
      <div className="flex items-center justify-between">
        <div>
          <h2 className="text-lg font-semibold flex items-center gap-2">
            <Shield className="h-5 w-5 text-brand" />
            Disputifier Enrollments
          </h2>
          {merchants.length > 0 && (
            <p className="text-sm text-muted-foreground mt-1">
              {merchants.length} DBA{merchants.length !== 1 ? "s" : ""},{" "}
              {merchants.reduce((sum, m) => sum + (m.mids?.length || 0), 0)} MID
              {merchants.reduce((sum, m) => sum + (m.mids?.length || 0), 0) !== 1 ? "s" : ""} enrolled
            </p>
          )}
        </div>
        {isAdmin && !showEnrollForm && (
          <button
            className="btn btn-primary btn-sm"
            onClick={() => {
              fetchDbas();
              fetchMids();
              setShowEnrollForm(true);
            }}
          >
            <Plus className="h-4 w-4" />
            Enroll DBA
          </button>
        )}
      </div>

      {/* Enroll form */}
      {isAdmin && showEnrollForm && (
        <div className="surface rounded-xl ring-1 ring-border p-6 space-y-4">
          <h3 className="text-sm font-medium">Enroll DBA in Disputifier</h3>
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
            <div>
              <label className="text-sm font-medium mb-1 block">DBA</label>
              <select
                className="select select-bordered w-full"
                value={enrollForm.dbaId}
                onChange={(e) => setEnrollForm({ ...enrollForm, dbaId: e.target.value })}
              >
                <option value="">Select DBA...</option>
                {dbas
                  .filter((d) => !merchants.some((m) => m.dbaId === d.dbaId))
                  .map((dba) => (
                    <option key={dba.dbaId} value={dba.dbaId}>{dba.dbaName}</option>
                  ))}
              </select>
            </div>
            <div>
              <label className="text-sm font-medium mb-1 block">Initial MID</label>
              <select
                className="select select-bordered w-full"
                value={enrollForm.midId}
                onChange={(e) => setEnrollForm({ ...enrollForm, midId: e.target.value })}
              >
                <option value="">Select MID...</option>
                {mids.map((m) => (
                  <option key={m.midId} value={m.midId}>{m.mid} — {m.descriptor}</option>
                ))}
              </select>
            </div>
            <div>
              <label className="text-sm font-medium mb-1 block">Email</label>
              <input
                type="email"
                className="input input-bordered w-full"
                placeholder="Contact email"
                value={enrollForm.email}
                onChange={(e) => setEnrollForm({ ...enrollForm, email: e.target.value })}
              />
            </div>
            <div>
              <label className="text-sm font-medium mb-1 block">Threshold Amount</label>
              <input
                type="number"
                className="input input-bordered w-full"
                placeholder="Max refund amount"
                value={enrollForm.thresholdAmount}
                onChange={(e) => setEnrollForm({ ...enrollForm, thresholdAmount: e.target.value })}
              />
            </div>
          </div>
          <div>
            <label className="text-sm font-medium mb-2 block">Programs</label>
            <div className="flex flex-wrap gap-4">
              {PROGRAMS.map((p) => (
                <label key={p} className="flex items-center gap-2 text-sm">
                  <input
                    type="checkbox"
                    checked={enrollForm.programs[p]}
                    onChange={(e) =>
                      setEnrollForm({
                        ...enrollForm,
                        programs: { ...enrollForm.programs, [p]: e.target.checked },
                      })
                    }
                  />
                  {p.toUpperCase()}
                </label>
              ))}
            </div>
          </div>
          <div className="flex gap-2">
            <button
              className="btn btn-primary btn-sm"
              onClick={handleEnroll}
              disabled={submitting || !enrollForm.dbaId || !enrollForm.midId}
            >
              <Save className="h-4 w-4" />
              {submitting ? "Enrolling..." : "Enroll"}
            </button>
            <button className="btn btn-ghost btn-sm" onClick={() => setShowEnrollForm(false)}>
              <X className="h-4 w-4" /> Cancel
            </button>
          </div>
        </div>
      )}

      {/* Merchant cards */}
      {merchants.length === 0 ? (
        <div className="surface rounded-xl ring-1 ring-border p-8 text-center">
          <Shield className="mx-auto h-10 w-10 text-muted-foreground mb-3" />
          <p className="text-sm text-muted-foreground">
            No DBAs enrolled in Disputifier for this company
          </p>
        </div>
      ) : (
        merchants.map((merchant) => (
          <div key={merchant.dbaId} className="surface rounded-xl ring-1 ring-border p-6 space-y-4">
            <div className="flex items-center justify-between">
              <div>
                <h3 className="text-base font-semibold">{merchant.dbaName}</h3>
                <p className="text-sm text-muted-foreground">
                  {merchant.email && <>{merchant.email} &middot; </>}
                  {merchant.thresholdAmount != null && <>Threshold: ${merchant.thresholdAmount}</>}
                </p>
              </div>
              <span className="text-xs text-muted-foreground">
                {merchant.mids?.length || 0} MID{(merchant.mids?.length || 0) !== 1 ? "s" : ""}
              </span>
            </div>

            {merchant.mids?.length > 0 && (
              <div className="overflow-x-auto">
                <table className="w-full">
                  <thead>
                    <tr>
                      {["MID", "Descriptor", "RDR", "CDRN", "Ethoca", "TC40", "Status"].map((col) => (
                        <th
                          key={col}
                          className="px-3 py-2 text-left text-xs font-medium text-muted-foreground uppercase tracking-wider"
                        >
                          {col}
                        </th>
                      ))}
                    </tr>
                  </thead>
                  <tbody className="divide-y divide-border">
                    {merchant.mids.map((midEntry) => (
                      <tr
                        key={midEntry.midId}
                        className="hover:bg-muted/40 cursor-pointer transition-colors"
                        onClick={() => navigate(`/mids/${midEntry.midId}`)}
                      >
                        <td className="px-3 py-2 text-sm font-medium text-foreground">{midEntry.mid}</td>
                        <td className="px-3 py-2 text-sm text-muted-foreground">{midEntry.descriptor || "—"}</td>
                        {PROGRAMS.map((p) => (
                          <td key={p} className="px-3 py-2">
                            <ProgramBadge
                              status={midEntry.programs?.[p]?.status}
                              program={p}
                              enrolled={midEntry.programs?.[p]?.enrolled}
                              midStatus={midEntry.status}
                            />
                          </td>
                        ))}
                        <td className="px-3 py-2">
                          <StatusBadge status={midEntry.status} />
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            )}
          </div>
        ))
      )}

      {/* Disputifier-enrollment tickets across this company's enrolled MIDs
          (reverse lookup — SOUND-497; enrollments are keyed by midId).
          Internal-only; hideWhenEmpty keeps the tab clean until tickets exist. */}
      {isAdmin && (() => {
        const enrolledMidIds = merchants
          .flatMap((m) => (m.mids || []).map((x) => x.midId))
          .filter(Boolean);
        if (enrolledMidIds.length === 0) return null;
        return (
          <EntityTicketsSection
            entities={enrolledMidIds.map((id) => ({
              entityType: "disputifierEnrollment",
              entityId: id,
            }))}
            title="Disputifier tickets"
            hideWhenEmpty
          />
        );
      })()}
    </div>
  );
}
